/**
 * DESIGN SYSTEM STEP 3 — a THEMED page, read-only: the customer's preview card
 * (AlbumFlowPage) and the parity test page draw it with this. Same layers and
 * the same rules as the editor (PageView) and the print (pdfGenerator):
 *   paper → (shadow, element) in shadowPlan order → nothing that does not print.
 * The page box establishes `container-type: inline-size`, so text sizes in cqi
 * (fontSize/4) match the print exactly (storiz-render-parity skill).
 */
import { useState, type CSSProperties } from 'react';
import { PageBackgroundLayer, ElementShadow, photoMaskStyle, type ShadowElement } from './PageDecor';
import { shadowPlan } from '../../lib/decor/decorStyle';
import { printable } from '../../lib/decor/holders';
import { printOpticalSize } from '../../lib/pdfTextLayout';
import { fontStackFor } from '../../lib/textBidi';
import { hasStroke, strokeLayerCss, fillOpacity, RECT_FILL_DEFAULT } from '../../lib/decor/shapePaint';
import { cropCss } from '../../lib/decor/cropMath';
import { TintFilter, tintCss } from './TintFilter';
import { bandCss } from '../../lib/textBand';

export interface ThemedElement extends ShadowElement {
  type: string;
  photoId?: string;
  imageUrl?: string;
  fit?: 'fill';
  crop?: { x: number; y: number; w: number; h: number };
  opacity?: number;
  text?: string;
  holder?: string;
  labelFor?: string;
  fontFamily?: string;
  fontSize?: number;
  fontColor?: string;
  fontWeight?: string;
  fontStyle?: string;
  textAlign?: 'left' | 'center' | 'right';
  /** A caption ON a photo (captionLayout.ts — a model page with no room on its paper): the
   *  outline around each letter (× the font size) and the book-colour band, as the editor and the print. */
  outlineColor?: string;
  outlineWidth?: number;
  bandFill?: string;
  bandOpacity?: number;
  /** rect (a model's line frame, design system v2): stroke in PDF points — a physical
   *  size (lib/decor/shapePaint.ts), like pdfGenerator. */
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  /** A TINTED piece (lib/decor/tint.ts): its file is a colour map drawn in these four colours. */
  tint?: readonly string[];
}

function transformOf(e: ThemedElement): CSSProperties {
  const rot = e.rotation ? `rotate(${e.rotation}deg)` : '';
  const flip = e.type !== 'text' && (e.flipH || e.flipV) ? `scaleX(${e.flipH ? -1 : 1}) scaleY(${e.flipV ? -1 : 1})` : '';
  return {
    position: 'absolute', left: `${e.x}%`, top: `${e.y}%`, width: `${e.width}%`, height: `${e.height}%`,
    transform: [rot, flip].filter(Boolean).join(' ') || undefined, transformOrigin: 'center', zIndex: 1010,
  };
}

/** A photo element: the saved crop window cover-fitted into the box — the
 *  editor's PageElementOverlay math (parity with the PDF's canvas pre-crop). */
function PhotoLayer({ e, url }: { e: ThemedElement; url: string | null }) {
  const [nat, setNat] = useState<{ w: number; h: number } | null>(null);
  const boxAR = (e.width * 2) / (e.height * 2.97);   // % of 200 × 297 → real aspect
  const cr = e.crop;
  let style: CSSProperties = { width: '100%', height: '100%', objectFit: 'cover', opacity: e.opacity ?? 1 };
  if (cr && cr.w > 0 && cr.h > 0 && nat) {
    const c = cropCss(cr, nat, boxAR);    // lib/decor/cropMath.ts — the print's cropSource, in CSS
    style = {
      position: 'absolute', maxWidth: 'none', opacity: e.opacity ?? 1,
      left: `${c.left * 100}%`, top: `${c.top * 100}%`, width: `${c.width * 100}%`, height: `${c.height * 100}%`,
    };
  }
  return (
    <div style={transformOf(e)}>
      <div className="w-full h-full relative overflow-hidden" style={photoMaskStyle(e.maskUrl)}>
        {url && <img src={url} alt="" draggable={false} style={style}
          onLoad={(ev) => { const im = ev.currentTarget; if (im.naturalWidth) setNat({ w: im.naturalWidth, h: im.naturalHeight }); }} />}
      </div>
    </div>
  );
}

export function ThemedPageView({ page, photoUrl }: {
  page: { background?: { imageUrl?: string | null; previewUrl?: string | null; color?: string | null; tint?: readonly string[] | null } | null; elements?: ThemedElement[] | null; blank_color?: string | null };
  photoUrl: (photoId: string) => string | null;
}) {
  const els = printable(page.elements ?? []);
  const plan = shadowPlan(els);
  return (
    <div className="absolute inset-0 overflow-hidden" style={{ containerType: 'inline-size', background: page.blank_color ?? '#fff' }} data-themed-page>
      <PageBackgroundLayer background={page.background} />
      {els.map((e, i) => (
        <div key={e.id} style={{ display: 'contents' }}>
          {(plan.get(i) ?? []).map((s) => <ElementShadow key={`sh-${s.id}`} el={s} />)}
          {e.type === 'photo' ? <PhotoLayer e={e} url={e.photoId ? photoUrl(e.photoId) : null} />
            : e.type === 'icon' ? (
              <div style={transformOf(e)}>
                <TintFilter stops={e.tint} />
                <img src={e.previewUrl ?? e.imageUrl} alt="" draggable={false} className="w-full h-full"
                  style={{ objectFit: e.fit === 'fill' ? 'fill' : 'contain', opacity: e.opacity ?? 1, ...tintCss(e.tint) }} />
              </div>
            ) : e.type === 'rect' ? (
              // A model's line frame: the print's fill, then the line — a
              // physical width centred on the edge (lib/decor/shapePaint.ts,
              // the same function the editor and the PDF take it from).
              <div style={transformOf(e)} data-shape-rect="">
                <div className="absolute inset-0" style={{ background: e.fill ?? RECT_FILL_DEFAULT, opacity: fillOpacity(e, RECT_FILL_DEFAULT) }} />
                {hasStroke(e) && <div style={strokeLayerCss(e)!} />}
              </div>
            ) : e.type === 'text' ? (
              <div style={{ ...transformOf(e), display: 'flex', alignItems: 'center',
                justifyContent: e.textAlign === 'left' ? 'flex-start' : e.textAlign === 'right' ? 'flex-end' : 'center' }}>
                {bandCss(e) && <div aria-hidden style={{ position: 'absolute', inset: 0, background: bandCss(e) }} />}
                <span style={{ position: 'relative',
                  // Arabic letters in a NAMED Arabic face and each line in its own direction —
                  // as the editor (TextOverlayChrome) and the print raster (lib/textBidi.ts) do.
                  fontFamily: fontStackFor(e.fontFamily, e.text), fontSize: `${(e.fontSize ?? 18) / 4}cqi`, fontVariationSettings: printOpticalSize(e.fontSize), color: e.fontColor,
                  fontWeight: e.fontWeight as CSSProperties['fontWeight'], fontStyle: e.fontStyle as CSSProperties['fontStyle'],
                  textAlign: e.textAlign ?? 'center', lineHeight: 1.3, whiteSpace: 'pre-wrap', overflowWrap: 'break-word', width: '100%',
                  unicodeBidi: 'plaintext',
                  ...(e.outlineColor && (e.outlineWidth ?? 0) > 0 ? { WebkitTextStroke: `${2 * (e.outlineWidth as number)}em ${e.outlineColor}`, paintOrder: 'stroke fill' } : {}),
                }}>{e.text}</span>
              </div>
            ) : null}
        </div>
      ))}
    </div>
  );
}
